import { ColumnDef, Row } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { getLessonReportDateTime } from '@/helpers/lessonReportDateTime'
import {
  IconButtonCell,
  TextCell,
  TextIconButtonCell,
} from '@pages/Admin/Reports/Payments/components/Cells/Cells'
import { IGroupLessonPaymentByLessonItem } from '../../utils/types'
import cn from './index.module.css'
import useRoles from '@/hooks/useRoles'
import { DownOutlined, EditOutlined, InfoCircleOutlined, UpOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { useFranchises } from '@/hooks/useFranchises'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { getCompactStatusSymbol, getStatusColor, getStatusDescription } from '../../utils/lessonStatus'

interface TableColumnsProps {
  onSeminarianClick: (user: IUser) => void
  onChangeStatusClick: (lesson: IGroupLessonPaymentByLessonItem) => void
  onIncomeClick: (lesson: IGroupLessonPaymentByLessonItem) => void
  showStudentStatus: boolean
}

const StatusHeader = ({ title }: { title: string }) => (
  <TooltipWrapper title={title}>
    <span aria-label={title} tabIndex={0}>
      <InfoCircleOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
    </span>
  </TooltipWrapper>
)

const getProfit = (income?: number | null, salary?: number | null) => {
  if (income === null || income === undefined) return null
  if (salary === null || salary === undefined) return null

  return income - salary
}

const formatComments = (lesson: IGroupLessonPaymentByLessonItem) => {
  if (Array.isArray(lesson.comments) && lesson.comments.length > 0) {
    return lesson.comments.map((item) => ({
      key: `${item.student_id}-${item.comment}`,
      text: `${item.student_name}\n${getStatusDescription(
        String(item.status),
      )}\n${item.comment}`,
    }))
  }

  return lesson.comment ? [{ key: 'comment', text: lesson.comment }] : []
}

const useTableColumns = ({
  onSeminarianClick,
  onChangeStatusClick,
  onIncomeClick,
  showStudentStatus,
}: TableColumnsProps) => {
  const { isAdminOrJuniorAdmin } = useRoles()
  const { enabled: franchisesEnabled } = useFranchises()
  const [expandedComments, setExpandedComments] = useState<
    Record<number, boolean>
  >({})
  const settings = useSelector((store: any) => ({
    teacherCanEdit: store.settings?.isTeacherCanEditStatuses === '1',
  }))

  return useMemo<ColumnDef<IGroupLessonPaymentByLessonItem>[]>(() => {
    const columns: ColumnDef<IGroupLessonPaymentByLessonItem>[] = [
      {
        header: '',
        id: 'index',
        size: 40,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell
            text={String(row.original.index ?? row.index + 1)}
            styles={{ textAlign: 'center' }}
          />
        ),
      },
      {
        id: 'change-status',
        size: 70,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <IconButtonCell
            onClick={() => onChangeStatusClick(row.original)}
            icon={
              <EditOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            }
          />
        ),
      },
      ...(showStudentStatus
        ? [
            {
              header: () => <StatusHeader title="Статус учащегося" />,
              id: 'student-status',
              size: 72,
              cell: ({
                row,
              }: {
                row: Row<IGroupLessonPaymentByLessonItem>
              }) => (
                <TooltipWrapper
                  title={getStatusDescription(
                    String(row.original.student_status),
                  )}
                >
                  <div style={{ height: '100%', backgroundColor: getStatusColor(String(row.original.student_status)) }}>
                  <TextCell
                    text={getCompactStatusSymbol(
                      String(row.original.student_status),
                    )}
                    wrapperStyles={{
                      backgroundColor: getStatusColor(
                        String(row.original.student_status),
                      ),
                    }}
                  />
                  </div>
                </TooltipWrapper>
              ),
            },
          ]
        : []),
      {
        header: 'Дата и время занятия',
        id: 'date-time',
        size: 170,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell
            text={getLessonReportDateTime(row.original).text}
            styles={{ whiteSpace: 'pre-line' }}
          />
        ),
      },
      {
        header: 'Педагог',
        id: 'seminarian',
        size: 190,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell
            text={row.original.seminarian?.name ?? '-'}
            styles={{ textDecoration: 'underline' }}
            onClick={() => onSeminarianClick(row.original.seminarian)}
          />
        ),
      },
      {
        header: 'Курс',
        id: 'course',
        size: 180,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell
            text={row.original.course?.name ?? '-'}
            styles={{ width: '100%', textAlign: 'start' }}
          />
        ),
      },
      {
        header: 'Группа',
        id: 'group',
        size: 160,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell
            text={row.original.group?.name ?? '-'}
            styles={{ width: '100%', textAlign: 'start' }}
          />
        ),
      },
      {
        header: 'Филиал',
        id: 'franchise',
        size: 180,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={row.original.franchise?.name ?? '-'} />
        ),
      },
      {
        header: () => <StatusHeader title="Статус занятия" />,
        id: 'status',
        size: 72,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TooltipWrapper
            title={getStatusDescription(String(row.original.status))}
          >
            <div style={{ height: '100%', backgroundColor: getStatusColor(String(row.original.status)) }}>
            <TextCell
              text={getCompactStatusSymbol(String(row.original.status))}
              wrapperStyles={{
                backgroundColor: getStatusColor(String(row.original.status)),
              }}
            />
            </div>
          </TooltipWrapper>
        ),
      },
      {
        header: 'Длительность',
        id: 'duration',
        size: 120,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={row.original.duration ?? '-'} />
        ),
      },
      {
        header: 'Кол-во учащихся в группе',
        id: 'group-student-count',
        size: 170,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={String(row.original.group_student_count ?? '-')} />
        ),
      },
      {
        header: 'Проведено для',
        id: 'visited-count',
        size: 140,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={String(row.original.visited_count ?? '-')} />
        ),
      },
      {
        header: 'Не пришло',
        id: 'missed-count',
        size: 120,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={String(row.original.missed_count ?? '-')} />
        ),
      },
      {
        header: 'Доход',
        id: 'income',
        size: 160,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextIconButtonCell
            text={String(row.original.income ?? '-')}
            onClick={async () => onIncomeClick(row.original)}
          />
        ),
      },
      {
        header: 'ЗП',
        id: 'salary',
        size: 160,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell text={String(row.original.salary ?? '-')} />
        ),
      },
      {
        header: 'Прибыль',
        id: 'profit',
        size: 160,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => (
          <TextCell
            text={String(
              getProfit(row.original.income, row.original.salary) ?? '-',
            )}
          />
        ),
      },
      {
        header: 'Комментарий',
        id: 'comment',
        size: 300,
        cell: ({ row }: { row: Row<IGroupLessonPaymentByLessonItem> }) => {
          const comments = formatComments(row.original)
          const isExpanded = Boolean(expandedComments[row.original.id])
          const hasOverflow =
            comments.length > 1 || (comments[0]?.text?.length ?? 0) > 80
          const visibleComments = isExpanded ? comments : comments.slice(0, 1)

          if (comments.length === 0) {
            return <TextCell text="-" />
          }

          return (
            <div
              className={cn.commentCell}
              data-expanded={isExpanded ? 'true' : 'false'}
            >
              <div className={cn.commentText}>
                {visibleComments.map((comment) => (
                  <div key={comment.key} className={cn.commentLine}>
                    {comment.text}
                  </div>
                ))}
              </div>
              {hasOverflow && (
                <button
                  type="button"
                  className={cn.commentToggle}
                  onClick={() =>
                    setExpandedComments((prev) => ({
                      ...prev,
                      [row.original.id]: !prev[row.original.id],
                    }))
                  }
                  title={isExpanded ? 'Свернуть' : 'Показать полностью'}
                >
                  {isExpanded ? (
                    <UpOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  ) : (
                    <DownOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  )}
                </button>
              )}
            </div>
          )
        },
      },
    ]

    const visibleColumns = franchisesEnabled
      ? columns
      : columns.filter((column) => column.id !== 'franchise')

    if (isAdminOrJuniorAdmin) return visibleColumns

    return visibleColumns
      .filter((column) => !['profit', 'income'].includes(String(column.id)))
      .filter((column) => {
        return String(column.id) === 'change-status'
          ? settings.teacherCanEdit
          : true
      })
  }, [
    onSeminarianClick,
    onChangeStatusClick,
    onIncomeClick,
    isAdminOrJuniorAdmin,
    expandedComments,
    settings,
    franchisesEnabled,
    showStudentStatus,
  ])
}

export default useTableColumns
